import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { AdminPageHeader } from "@/components/admin/crud-helpers";
import {
  AlertTriangle, Boxes, PackageCheck, PackageX, TrendingDown, TrendingUp,
  Plus, Minus, RotateCcw, Search, Download, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/inventory")({
  component: InventoryPage,
});

type VariantRow = {
  id: string;
  name: string;
  stock_qty: number;
  stock_tracked: boolean;
  low_stock_threshold: number;
  product_id: string;
  active: boolean;
  price_cents: number;
  currency: string;
  products?: { name: string; kind: string; slug: string } | null;
};

type Movement = {
  id: string;
  variant_id: string;
  delta: number;
  reason: string;
  created_at: string;
  product_variants?: { name: string; products?: { name: string } | null } | null;
};

function InventoryPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState<string>("all");
  const [stockFilter, setStockFilter] = useState<"all" | "low" | "out" | "ok">("all");
  const [adjustOpen, setAdjustOpen] = useState<VariantRow | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["inventory_variants"] }),
        qc.invalidateQueries({ queryKey: ["inventory_movements"] }),
      ]);
      setLastSyncAt(new Date());
      toast.success("Inventory synced");
    } catch (e: any) {
      toast.error(e?.message ?? "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const { data: variants = [], isLoading } = useQuery({
    queryKey: ["inventory_variants"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_variants")
        .select("id, name, stock_qty, stock_tracked, low_stock_threshold, product_id, active, price_cents, currency, products(name, kind, slug)")
        .eq("stock_tracked", true)
        .order("stock_qty");
      if (error) throw error;
      return data as unknown as VariantRow[];
    },
  });

  const { data: movements = [] } = useQuery({
    queryKey: ["inventory_movements"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("stock_movements")
        .select("id, variant_id, delta, reason, created_at, product_variants(name, products(name))")
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return data as unknown as Movement[];
    },
  });

  const stats = useMemo(() => {
    const total = variants.length;
    const out = variants.filter((v) => v.stock_qty <= 0).length;
    const low = variants.filter((v) => v.stock_qty > 0 && v.stock_qty <= v.low_stock_threshold).length;
    const ok = total - out - low;
    const value = variants.reduce((s, v) => s + v.stock_qty * v.price_cents, 0) / 100;
    return { total, out, low, ok, value };
  }, [variants]);

  const kinds = useMemo(() => {
    const s = new Set<string>();
    variants.forEach((v) => v.products?.kind && s.add(v.products.kind));
    return Array.from(s);
  }, [variants]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return variants.filter((v) => {
      if (kindFilter !== "all" && v.products?.kind !== kindFilter) return false;
      if (stockFilter === "out" && v.stock_qty > 0) return false;
      if (stockFilter === "low" && !(v.stock_qty > 0 && v.stock_qty <= v.low_stock_threshold)) return false;
      if (stockFilter === "ok" && v.stock_qty <= v.low_stock_threshold) return false;
      if (!needle) return true;
      return (
        v.name.toLowerCase().includes(needle) ||
        (v.products?.name ?? "").toLowerCase().includes(needle) ||
        (v.products?.slug ?? "").toLowerCase().includes(needle)
      );
    });
  }, [variants, q, kindFilter, stockFilter]);

  const exportCsv = () => {
    const rows = [
      ["Product", "Variant", "Kind", "Stock", "Threshold", "Status", "Unit price", "Currency"],
      ...filtered.map((v) => [
        v.products?.name ?? "",
        v.name,
        v.products?.kind ?? "",
        v.stock_qty,
        v.low_stock_threshold,
        v.stock_qty <= 0 ? "Out" : v.stock_qty <= v.low_stock_threshold ? "Low" : "OK",
        (v.price_cents / 100).toFixed(2),
        v.currency,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `inventory-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Inventory & stock"
        description="Stock levels across all tracked product variants. Adjust quantities, set reorder thresholds and audit movement history."
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard icon={Boxes} label="Tracked variants" value={stats.total} tone="default" />
        <StatCard icon={PackageCheck} label="In stock" value={stats.ok} tone="success" />
        <StatCard icon={TrendingDown} label="Low stock" value={stats.low} tone="warning" />
        <StatCard icon={PackageX} label="Out of stock" value={stats.out} tone="danger" />
        <StatCard icon={TrendingUp} label="Stock value" value={`$${stats.value.toFixed(0)}`} tone="default" />
      </div>

      {stats.low + stats.out > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-500" />
          <div>
            <strong>{stats.out} out of stock</strong> and <strong>{stats.low} low</strong> — replenish soon to avoid lost orders.
          </div>
        </div>
      )}

      <Tabs defaultValue="stock">
        <PillTabsList search>
          <PillTabsTrigger value="stock">Stock levels</PillTabsTrigger>
          <PillTabsTrigger value="movements">Movement history</PillTabsTrigger>
        </PillTabsList>

        <TabsContent value="stock" className="space-y-3 pt-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search product or variant…"
                className="pl-9"
              />
            </div>
            <Select value={kindFilter} onValueChange={setKindFilter}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Kind" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All kinds</SelectItem>
                {kinds.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={stockFilter} onValueChange={(v) => setStockFilter(v as any)}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All stock</SelectItem>
                <SelectItem value="ok">In stock</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="out">Out of stock</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={exportCsv} className="gap-1.5"><Download className="h-4 w-4" /> CSV</Button>
            <Button variant="outline" onClick={handleSync} disabled={syncing} className="gap-1.5">
              <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing…" : "Sync"}
            </Button>
            {lastSyncAt && (
              <span className="text-xs text-muted-foreground">
                Last synced {lastSyncAt.toLocaleTimeString()}
              </span>
            )}
          </div>

          <div className="overflow-hidden rounded-lg border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5">Product</th>
                  <th className="px-4 py-2.5">Variant</th>
                  <th className="px-4 py-2.5">Kind</th>
                  <th className="px-4 py-2.5 text-right">Stock</th>
                  <th className="px-4 py-2.5 text-right">Threshold</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>
                )}
                {!isLoading && filtered.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                    No tracked variants match your filters. Enable "Stock tracked" on a product variant to see it here.
                  </td></tr>
                )}
                {filtered.map((v) => {
                  const status = v.stock_qty <= 0 ? "out" : v.stock_qty <= v.low_stock_threshold ? "low" : "ok";
                  return (
                    <tr key={v.id} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-2.5">
                        <div className="font-medium">{v.products?.name ?? "—"}</div>
                        {v.products?.slug && <div className="text-xs text-muted-foreground">/{v.products.slug}</div>}
                      </td>
                      <td className="px-4 py-2.5">{v.name}</td>
                      <td className="px-4 py-2.5"><Badge variant="outline" className="capitalize">{v.products?.kind ?? "—"}</Badge></td>
                      <td className="px-4 py-2.5 text-right font-mono font-semibold">{v.stock_qty}</td>
                      <td className="px-4 py-2.5 text-right text-muted-foreground">{v.low_stock_threshold}</td>
                      <td className="px-4 py-2.5">
                        {status === "out" && <Badge variant="destructive">Out of stock</Badge>}
                        {status === "low" && <Badge className="bg-amber-500/15 text-amber-600 hover:bg-amber-500/20">Low</Badge>}
                        {status === "ok" && <Badge variant="secondary">In stock</Badge>}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <Button size="sm" variant="outline" onClick={() => setAdjustOpen(v)}>Adjust</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="movements" className="pt-4">
          <div className="overflow-hidden rounded-lg border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5">Date</th>
                  <th className="px-4 py-2.5">Variant</th>
                  <th className="px-4 py-2.5 text-right">Delta</th>
                  <th className="px-4 py-2.5">Reason</th>
                </tr>
              </thead>
              <tbody>
                {movements.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">No movements yet.</td></tr>
                )}
                {movements.map((m) => (
                  <tr key={m.id} className="border-t border-border">
                    <td className="px-4 py-2.5 text-muted-foreground">{new Date(m.created_at).toLocaleString()}</td>
                    <td className="px-4 py-2.5">
                      {m.product_variants?.products?.name ?? "—"} <span className="text-muted-foreground">/ {m.product_variants?.name}</span>
                    </td>
                    <td className={`px-4 py-2.5 text-right font-mono font-semibold ${m.delta > 0 ? "text-emerald-600" : "text-rose-600"}`}>
                      {m.delta > 0 ? `+${m.delta}` : m.delta}
                    </td>
                    <td className="px-4 py-2.5"><Badge variant="outline" className="capitalize">{m.reason}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <AdjustStockDialog
        variant={adjustOpen}
        onClose={() => setAdjustOpen(null)}
        onDone={() => {
          qc.invalidateQueries({ queryKey: ["inventory_variants"] });
          qc.invalidateQueries({ queryKey: ["inventory_movements"] });
        }}
      />
    </div>
  );
}

function StatCard({
  icon: Icon, label, value, tone,
}: { icon: any; label: string; value: number | string; tone: "default" | "success" | "warning" | "danger" }) {
  const toneClass = {
    default: "text-foreground",
    success: "text-emerald-600",
    warning: "text-amber-600",
    danger: "text-rose-600",
  }[tone];
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xs font-medium text-muted-foreground">{label}</CardTitle>
        <Icon className={`h-4 w-4 ${toneClass}`} />
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${toneClass}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function AdjustStockDialog({
  variant, onClose, onDone,
}: { variant: VariantRow | null; onClose: () => void; onDone: () => void }) {
  const [mode, setMode] = useState<"add" | "remove" | "set">("add");
  const [qty, setQty] = useState<number>(1);
  const [threshold, setThreshold] = useState<number>(0);
  const [reason, setReason] = useState<string>("restock");
  const [note, setNote] = useState<string>("");

  // reset when variant changes
  useMemo(() => {
    if (variant) {
      setMode("add"); setQty(1); setThreshold(variant.low_stock_threshold); setReason("restock"); setNote("");
    }
  }, [variant?.id]);

  const mutate = useMutation({
    mutationFn: async () => {
      if (!variant) return;
      const current = variant.stock_qty;
      const newQty = mode === "add" ? current + qty : mode === "remove" ? current - qty : qty;
      const delta = newQty - current;

      const { error: updErr } = await supabase
        .from("product_variants")
        .update({ stock_qty: newQty, low_stock_threshold: threshold })
        .eq("id", variant.id);
      if (updErr) throw updErr;

      if (delta !== 0) {
        const { error: movErr } = await supabase
          .from("stock_movements")
          .insert({
            variant_id: variant.id,
            delta,
            reason: note ? `${reason}: ${note}` : reason,
          });
        if (movErr) throw movErr;
      }
    },
    onSuccess: () => { toast.success("Stock updated"); onDone(); onClose(); },
    onError: (e: any) => toast.error(e.message ?? "Failed to update stock"),
  });

  const preview = !variant ? 0 :
    mode === "add" ? variant.stock_qty + qty :
    mode === "remove" ? variant.stock_qty - qty :
    qty;

  return (
    <Dialog open={!!variant} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Adjust stock</DialogTitle>
        </DialogHeader>
        {variant && (
          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
              <div className="font-medium">{variant.products?.name}</div>
              <div className="text-muted-foreground">{variant.name} · current stock <strong className="text-foreground">{variant.stock_qty}</strong></div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Button variant={mode === "add" ? "default" : "outline"} onClick={() => setMode("add")} className="gap-1.5"><Plus className="h-4 w-4" /> Add</Button>
              <Button variant={mode === "remove" ? "default" : "outline"} onClick={() => setMode("remove")} className="gap-1.5"><Minus className="h-4 w-4" /> Remove</Button>
              <Button variant={mode === "set" ? "default" : "outline"} onClick={() => setMode("set")} className="gap-1.5"><RotateCcw className="h-4 w-4" /> Set</Button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>{mode === "set" ? "New quantity" : "Quantity"}</Label>
                <Input type="number" min={0} value={qty} onChange={(e) => setQty(Math.max(0, Number(e.target.value) || 0))} />
              </div>
              <div className="grid gap-1.5">
                <Label>Low-stock threshold</Label>
                <Input type="number" min={0} value={threshold} onChange={(e) => setThreshold(Math.max(0, Number(e.target.value) || 0))} />
              </div>
            </div>

            <div className="grid gap-1.5">
              <Label>Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="restock">Restock</SelectItem>
                  <SelectItem value="manual">Manual adjustment</SelectItem>
                  <SelectItem value="recount">Recount / audit</SelectItem>
                  <SelectItem value="damage">Damage / loss</SelectItem>
                  <SelectItem value="return">Customer return</SelectItem>
                  <SelectItem value="transfer">Branch transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-1.5">
              <Label>Note (optional)</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. PO-1024 from supplier ACME" />
            </div>

            <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
              New stock: <strong className={preview < 0 ? "text-rose-600" : "text-foreground"}>{preview}</strong>
              {preview < 0 && <span className="ml-2 text-rose-600">Cannot go below zero</span>}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={mutate.isPending || preview < 0} onClick={() => mutate.mutate()}>
            {mutate.isPending ? "Saving…" : "Apply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
